/**
 * Parsed filters as chips under the search box.
 *
 * Finished filters can be removed one by one (or with Backspace in an empty
 * box). Filters still in the text are shown dashed, so it is clear what the
 * text is being read as before it is finished. A filter that cannot be
 * applied is red, says why, and is left out of the search.
 */
import { Group, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconX } from '@tabler/icons-react';
import { TOKENS, formatGBP } from '../../theme';
import { isoDayLabel } from '../../ui/format';
import type { Vocab } from '../../vocab';
import { chipErrorText } from './chipErrors';
import { PERSON_FIELDS, personKeyForColumn, type Chip, type Filter, type Op } from './query';
import { statusLabel } from './suggest';

export { chipErrorText };

function money(n: number): string {
  return formatGBP(n);
}

function span(from: string, to: string): string {
  return from === to ? isoDayLabel(from) : `${isoDayLabel(from)} to ${isoDayLabel(to)}`;
}

const PRESET_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  this_month: 'This month',
  last_month: 'Last month',
};

const OPERATOR_WORDS: Record<string, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  greater_than: '>',
  less_than: '<',
  greater_equal: '≥',
  less_equal: '≤',
  in: 'is',
  not_in: 'is not',
  starts_with: 'starts with',
};

/** A field as a chip names it: "First name" for a person field, else as stored. */
function fieldLabel(field: string): string {
  const person = personKeyForColumn(field);
  return person ? PERSON_FIELDS[person].label : field;
}

/** What a chip filters on, and what it asks for, in plain words. */
export function chipText(chip: Chip, v: Vocab): { key: string; value: string } {
  const not = chip.neg ? 'not ' : '';
  const f: Filter | null = chip.filter;
  if (!f) return { key: chip.keyText, value: chip.value || '…' };
  switch (f.kind) {
    case 'campaign': return { key: v.One('campaign'), value: `${not}${f.name}` };
    case 'supplier': return { key: v.One('supplier'), value: `${not}${f.id ? '' : 'named like '}${f.name}` };
    case 'buyer': return { key: v.One('buyer'), value: `${not}${f.id ? '' : 'named like '}${f.name}` };
    case 'status': return { key: 'Status', value: `${not}${statusLabel(f.status, v)}` };
    case 'date': return { key: 'Date', value: f.preset ? (PRESET_LABELS[f.preset] ?? f.preset) : span(f.span.from, f.span.to) };
    case 'after': return { key: 'From', value: isoDayLabel(f.day) };
    case 'before': return { key: 'Up to', value: isoDayLabel(f.day) };
    case 'value': {
      const value = f.min !== null && f.max !== null
        ? (f.min === f.max ? money(f.min) : `${money(f.min)} to ${money(f.max)}`)
        : f.min !== null ? `${money(f.min)} or more` : `${money(f.max ?? 0)} or less`;
      return { key: 'Value', value };
    }
    case 'source': return { key: v.p.sourceTag, value: `${not}${f.value}` };
    case 'repeats': return { key: `${v.One('person')} has`, value: `${f.min} or more ${v.many('lead')}` };
    case 'owner': return {
      key: v.One('owner'),
      value: f.owner === 'me' ? 'Me' : f.owner === 'none' ? 'Nobody' : f.name,
    };
    case 'score': {
      const value = f.min !== null && f.max !== null
        ? (f.min === f.max ? `${f.min}` : `${f.min} to ${f.max}`)
        : f.min !== null ? `${f.min} or more` : `${f.max ?? 0} or less`;
      return { key: 'Score', value };
    }
    case 'band': return { key: 'Score band', value: f.name };
    case 'field': {
      const key = fieldLabel(f.field);
      if (f.operator === 'is_not_empty') return { key, value: 'filled in' };
      if (f.operator === 'is_empty') return { key, value: 'empty' };
      return { key, value: `${OPERATOR_WORDS[f.operator] ?? f.operator} ${f.value}` };
    }
  }
}

/**
 * One thing in the chip row. `index` is a finished term's place in the bar's
 * list, so it can be removed; pending items are still in the box.
 */
export type RowItem =
  | { kind: 'chip'; chip: Chip; pending: boolean; index?: number }
  | { kind: 'op'; op: Op; pending: boolean }
  | { kind: 'text'; text: string; pending: boolean };

export function ChipRow({
  items,
  onRemove,
  v,
}: {
  items: RowItem[];
  onRemove: (index: number) => void;
  v: Vocab;
}) {
  if (items.length === 0) return null;
  return (
    <Group gap={6} px="md" pb={10} wrap="wrap" aria-label="Filters" role="list">
      {items.map((it, i) => {
        if (it.kind === 'op') return <OpView key={`o${i}`} op={it.op} />;
        if (it.kind === 'text') return <TextView key={`t${i}`} text={it.text} pending={it.pending} />;
        const index = it.index;
        return (
          <ChipView
            key={`c${i}`}
            chip={it.chip}
            v={v}
            pending={it.pending}
            onRemove={index === undefined ? undefined : () => onRemove(index)}
          />
        );
      })}
    </Group>
  );
}

/** OR as a small word between chips; brackets as brackets. */
function OpView({ op }: { op: Op }) {
  if (op === 'or') {
    return (
      <span
        role="listitem"
        data-testid="cmdk-or"
        style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', lineHeight: '18px', padding: '0 6px',
          borderRadius: 999, color: TOKENS.onAccent, background: TOKENS.accent,
        }}
      >
        OR
      </span>
    );
  }
  return (
    <span role="listitem" aria-label={op === '(' ? 'Group starts' : 'Group ends'} style={{ fontSize: 18, lineHeight: '20px', color: TOKENS.textMuted, fontWeight: 300 }}>
      {op}
    </span>
  );
}

/** Free text inside an OR, which is one side of it rather than the whole search. */
function TextView({ text, pending }: { text: string; pending: boolean }) {
  return (
    <div
      role="listitem"
      data-testid="cmdk-text"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 12,
        lineHeight: '18px', background: TOKENS.surface2, color: TOKENS.text,
        border: `1px ${pending ? 'dashed' : 'solid'} ${TOKENS.borderStrong}`,
      }}
    >
      <span style={{ color: TOKENS.textMuted }}>Text</span>
      <span style={{ fontWeight: 600 }}>{`“${text}”`}</span>
    </div>
  );
}

function ChipView({ chip, v, onRemove, pending = false }: { chip: Chip; v: Vocab; onRemove?: () => void; pending?: boolean }) {
  const { key, value } = chipText(chip, v);
  const bad = chip.error !== null;
  const body = (
    <div
      role="listitem"
      data-testid="cmdk-chip"
      data-error={bad || undefined}
      data-pending={pending || undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
        padding: onRemove ? '2px 4px 2px 8px' : '2px 8px', borderRadius: 999, fontSize: 12, lineHeight: '18px',
        background: bad ? TOKENS.redSoft : TOKENS.surface2,
        color: bad ? TOKENS.redText : TOKENS.text,
        border: `1px ${pending ? 'dashed' : 'solid'} ${bad ? TOKENS.red : TOKENS.borderStrong}`,
      }}
    >
      {bad && <IconAlertCircle size={13} aria-hidden />}
      <span style={{ color: bad ? TOKENS.redText : TOKENS.textMuted }}>{key}</span>
      <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
      {onRemove && (
        <UnstyledButton
          onMouseDown={(e) => e.preventDefault()}
          onClick={onRemove}
          aria-label={`Remove ${key} ${value}`}
          style={{ display: 'inline-flex', padding: 2, borderRadius: 999, color: 'inherit' }}
        >
          <IconX size={12} />
        </UnstyledButton>
      )}
    </div>
  );
  return bad && chip.error ? (
    <Tooltip label={chipErrorText(chip.error, v)} withArrow multiline w={260} zIndex={1000000}>
      {body}
    </Tooltip>
  ) : body;
}
